import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { RefreshCcw, Activity, TrendingUp, PiggyBank, AlertCircle, Play } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { automationService, insightService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316']

const InsightsPage = () => {
  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [automationStatus, setAutomationStatus] = useState(null)
  const [automationRunning, setAutomationRunning] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      const [dashboardData, statusData] = await Promise.all([
        insightService.getDashboard(),
        automationService.getStatus(),
      ])

      setDashboard(dashboardData)
      setAutomationStatus(statusData)
    } catch (error) {
      toast.error(error.message || 'Failed to load insights')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleRunAutomation = async () => {
    try {
      setAutomationRunning(true)
      const result = await automationService.run()
      toast.success(
        `Automation complete: ${result.results?.recurring_expenses_generated || 0} expenses, ${result.results?.recurring_incomes_generated || 0} incomes generated.`
      )
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to run automation')
    } finally {
      setAutomationRunning(false)
    }
  }

  const categoryData = useMemo(() => {
    const categories = dashboard?.spending_by_category?.categories || []
    return categories.slice(0, 8).map((item) => ({ name: item.category, value: item.amount }))
  }, [dashboard])

  const cashflowSeries = useMemo(() => {
    return dashboard?.cashflow?.series || []
  }, [dashboard])

  if (loading) {
    return <LoadingSpinner message="Crunching insights..." />
  }

  const overview = dashboard?.overview || {}
  const health = dashboard?.health_score || { score: 0, grade: 'N/A', tips: [] }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Financial Insights</h1>
          <p className="text-gray-600 dark:text-gray-400">Forecasts, health score, and automation control center.</p>
        </div>

        <div className="flex gap-2">
          <button onClick={loadData} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={handleRunAutomation}
            disabled={automationRunning}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-70"
          >
            <Play className="h-4 w-4" />
            {automationRunning ? 'Running...' : 'Run Automation'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Income</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatRupees(overview.month_income_total || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Expense</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatRupees(overview.month_expense_total || 0)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Net Worth</p>
          <p className={`text-2xl font-bold ${(overview.net_worth || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatRupees(overview.net_worth || 0)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Budget Utilization</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{(overview.budget_utilization_percent || 0).toFixed(1)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 inline-flex items-center gap-2">
            <Activity className="h-5 w-5" /> Financial Health
          </h2>

          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Health Score</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{health.score}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Grade: {health.grade}</p>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Recommendations</p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                {(health.tips || []).map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 text-amber-500" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="card lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 inline-flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Cashflow Trend
          </h2>

          {cashflowSeries.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No cashflow data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={cashflowSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatRupees(value)} />
                <Bar dataKey="income" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="net" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 inline-flex items-center gap-2">
            <PiggyBank className="h-5 w-5" /> Spending by Category
          </h2>

          {categoryData.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No category distribution available.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    dataKey="value"
                    label={({ name }) => name}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatRupees(value)} />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-2 mt-3">
                {categoryData.map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">{formatRupees(item.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automation Status</h2>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">Recurring Expense Templates</span>
              <span className="font-semibold text-gray-900 dark:text-white">{automationStatus?.recurring_expense_templates || 0}</span>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">Recurring Income Templates</span>
              <span className="font-semibold text-gray-900 dark:text-white">{automationStatus?.recurring_income_templates || 0}</span>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">Active Bills</span>
              <span className="font-semibold text-gray-900 dark:text-white">{automationStatus?.active_bills || 0}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last checked: {automationStatus?.last_checked_at ? new Date(automationStatus.last_checked_at).toLocaleString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InsightsPage
